import { useCallback, useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router";
import { useShallow } from "zustand/react/shallow";

import useBulkActionsFloatingBarHeight from "components/BulkActions/FloatingBar/hooks/useBulkActionsFloatingBarHeight";
import useShiftSelectionManager from "components/BulkActions/useShiftSelectionManager";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import { getSearchQuery } from "components/SearchInput/helpers";
import useTableSort from "components/Table/useTableSort";
import Box from "ds/components/Box";
import EmptyState from "ds/components/EmptyState";
import { selectSelectionIsStarted } from "ds/components/Table/store/useTableSelectionStore/selectors";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useErrorHandle from "hooks/useErrorHandle";
import { SearchQueryOrderDirection } from "types/generated";
import useSearchAiIntegrations from "shared/AiIntegration/useSearchAiIntegrations";

import { useLayoutActions } from "../../hooks/useLayoutActions";
import { useLayoutHeading } from "../../hooks/useLayoutHeading";
import { IntegrationView } from "../../types";
import AiIntegrationsBulkActions from "./components/BulkActions";
import AiCreateIntegrationButton from "./components/CreateIntegrationButton";
import { INTEGRATIONS_AI_TABLE_CELLS } from "./components/Table/Cells";
import useIntegrationsAiTableSelectionStore from "./components/Table/tableSelectionStore";
import useIntegrationsAiTableStore from "./components/Table/tableStore";
import useIntegrationsAiSelectionOptions from "./components/Table/useSelectionOptions";
import { AI_COLUMN_ID, AI_TABLE_FILTERS_STORAGE_KEY, SORT_CONFIG } from "./constants";
import AiIntegrationsEmpty from "./Empty";
import { useFilteredIntegrations } from "./useFilteredEntities";

const AiIntegrationsList = () => {
  const trackAnalytics = useAnalytics();

  const { initialLoading, error, integrations, refetch } = useSearchAiIntegrations();
  const { integration } = useOutletContext<{ integration: IntegrationView }>();

  useLayoutHeading(integration.name);

  useBreadcrumbs([
    {
      title: "Integrations",
      link: "/integrations",
    },
    {
      title: integration.name,
    },
  ]);

  useLayoutActions(<AiCreateIntegrationButton onCreated={refetch} />);

  const [searchParams] = useSearchParams();

  const isSearchActive = !!getSearchQuery(searchParams);

  const { sortDescriptor } = useTableSort({
    filtersOrderSettingsKey: AI_TABLE_FILTERS_STORAGE_KEY,
    sortConfig: SORT_CONFIG,
    initialSortOption: SORT_CONFIG[AI_COLUMN_ID.NAME],
    initialSortDirection: SearchQueryOrderDirection.Asc,
  });

  const filteredIntegrations = useFilteredIntegrations(integrations, sortDescriptor);

  const { selectionOptions, integrationsMap } =
    useIntegrationsAiSelectionOptions(filteredIntegrations);

  const { isBulkActionsActive, toggle, clearSelected, selectedSet, setAllItemsCount } =
    useIntegrationsAiTableSelectionStore(
      useShallow((state) => ({
        isBulkActionsActive: selectSelectionIsStarted(state),
        toggle: state.toggle,
        clearSelected: state.clearSelected,
        selectedSet: state.selected,
        setAllItemsCount: state.setAllItemsCount,
      }))
    );

  useEffect(() => {
    setAllItemsCount(filteredIntegrations.length);
  }, [setAllItemsCount, filteredIntegrations.length]);

  useEffect(() => {
    return () => {
      clearSelected();
    };
  }, [clearSelected]);

  const { height: floatingBarHeight, setHeight: setFloatingBarHeight } =
    useBulkActionsFloatingBarHeight(isBulkActionsActive);

  const shiftSelectionAction = useShiftSelectionManager({
    collection: filteredIntegrations,
    selectedIds: selectedSet,
  });

  const onBulkActionsFinish = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <TableSimple
        data={filteredIntegrations}
        loading={initialLoading}
        store={useIntegrationsAiTableStore}
        selectionStore={useIntegrationsAiTableSelectionStore}
        selectionOptions={selectionOptions}
        shiftSelectionAction={shiftSelectionAction}
        ariaLabel="AI integrations list"
        trackAnalytics={trackAnalytics}
        cellPerColumn={INTEGRATIONS_AI_TABLE_CELLS}
        filtersStorageKey={AI_TABLE_FILTERS_STORAGE_KEY}
        sortConfig={SORT_CONFIG}
        bottomOffset={floatingBarHeight}
        emptyState={
          isSearchActive ? (
            <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} announce />
          ) : (
            <AiIntegrationsEmpty />
          )
        }
      />

      <Box fullWidth relative>
        <AiIntegrationsBulkActions
          selectedSet={selectedSet}
          integrationsMap={integrationsMap}
          onBulkResetAll={clearSelected}
          onItemDismiss={toggle}
          onFinish={onBulkActionsFinish}
          onHeightChange={setFloatingBarHeight}
        />
      </Box>
    </>
  );
};

export default AiIntegrationsList;
